import { Check, ArrowLeft } from "lucide-react";

/**
 * InquiryStepper — a prominent guided-journey rail.
 *
 * Navigation rule
 * ---------------
 * A step is only reachable if it has already been visited/completed
 * (`idx <= highestCompletedIndex`). Users can freely jump BACK to any reached
 * step, but they CANNOT skip forward past the furthest completed step — moving
 * forward only happens via the step's own Continue button (which validates).
 *
 * On phone the whole rail sticks under the top bar so the current step, the
 * percentage and the clickable numbers stay visible while scrolling.
 */
export const InquiryStepper = ({ steps, activeIndex, highestCompletedIndex, onJump, onBack }) => {
    const pct = Math.round(((activeIndex + 1) / steps.length) * 100);
    const reached = Math.max(activeIndex, highestCompletedIndex);
    const go = (idx, isReachable, isActive) => {
        if (isReachable && !isActive) onJump?.(idx);
    };

    return (
        <>
            {/* Desktop — sticky vertical rail */}
            <aside className="hidden md:block" aria-label="Inquiry progress" data-testid="inquiry-stepper-desktop">
                <div className="pg-jrail sticky top-4">
                    <div className="pg-jstepper__head">
                        <span className="pg-eyebrow">Consultation</span>
                        <h3 className="font-display mt-3 text-2xl leading-tight" style={{ color: "var(--pg-fg)" }}>
                            Your guided journey
                        </h3>
                        <p className="mt-2 text-xs leading-relaxed" style={{ color: "var(--pg-fg-muted)" }}>
                            {steps.length} steps · about 4 minutes · tap a completed step to revisit it.
                        </p>
                        <div className="mt-4 flex items-center gap-3">
                            <button
                                type="button"
                                onClick={() => onBack?.()}
                                disabled={activeIndex === 0}
                                data-testid="inquiry-stepper-back"
                                className="pg-jstepper__back"
                            >
                                <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} /> Back
                            </button>
                            <span className="pg-jstepper__pct" data-testid="inquiry-progress-pct">{pct}%</span>
                        </div>
                    </div>

                    <ol className="pg-jsteps" role="list">
                        {steps.map((step, idx) => {
                            const isActive = idx === activeIndex;
                            const isDone = idx < activeIndex || idx <= highestCompletedIndex;
                            const isReachable = idx <= reached;
                            const isLast = idx === steps.length - 1;
                            const state = isActive ? "active" : isDone ? "done" : "todo";
                            return (
                                <li
                                    key={step.id}
                                    className="pg-jstep"
                                    data-state={state}
                                    data-reachable={isReachable ? "true" : "false"}
                                    data-testid={`step-item-${step.id}`}
                                >
                                    {!isLast && <span aria-hidden className="pg-jstep__line" data-fill={idx < reached ? "true" : "false"} />}
                                    <button
                                        type="button"
                                        onClick={() => go(idx, isReachable, isActive)}
                                        disabled={!isReachable}
                                        className="pg-jstep__node"
                                        aria-current={isActive ? "step" : undefined}
                                        aria-label={`Step ${idx + 1}: ${step.label}${isReachable ? "" : " (locked)"}`}
                                        data-testid={`step-node-${step.id}`}
                                    >
                                        {isActive && <span className="pg-jstep__pulse" aria-hidden="true" />}
                                        {isDone && !isActive ? <Check className="h-4 w-4" strokeWidth={2.4} /> : <span className="pg-jstep__no">{idx + 1}</span>}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => go(idx, isReachable, isActive)}
                                        disabled={!isReachable}
                                        className="pg-jstep__label"
                                        data-testid={`step-label-${step.id}`}
                                    >
                                        <span className="pg-jstep__title">{step.label}</span>
                                        <span className="pg-jstep__sub">{step.subtitle}</span>
                                    </button>
                                </li>
                            );
                        })}
                    </ol>
                </div>
            </aside>

            {/* Mobile — sticky bubble bar (current step + %) + clickable numbers */}
            <div className="md:hidden pg-jmobile" data-testid="inquiry-stepper-mobile">
                <div className="pg-jmobile__bar">
                    <span className="pg-jmobile__bubble" data-testid="inquiry-mobile-bubble">
                        <span className="pg-jmobile__bubble-no">{activeIndex + 1}</span>
                        <span className="pg-jmobile__bubble-total">/{steps.length}</span>
                    </span>
                    <div className="min-w-0 flex-1">
                        <div className="pg-jmobile__title" data-testid="step-mobile-title">{steps[activeIndex]?.label}</div>
                        <div className="pg-jmobile__meta">{pct}% complete · {steps[activeIndex]?.subtitle}</div>
                        <div className="pg-jmobile__progress"><span style={{ width: `${pct}%` }} /></div>
                    </div>
                    <button
                        type="button"
                        onClick={() => onBack?.()}
                        disabled={activeIndex === 0}
                        className="pg-jstepper__back"
                        data-testid="inquiry-stepper-back-mobile"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
                    </button>
                </div>
                <div className="pg-jdots" role="list">
                    {steps.map((step, idx) => {
                        const isActive = idx === activeIndex;
                        const isDone = idx < activeIndex || idx <= highestCompletedIndex;
                        const isReachable = idx <= reached;
                        const state = isActive ? "active" : isDone ? "done" : "todo";
                        return (
                            <button
                                key={step.id}
                                type="button"
                                onClick={() => go(idx, isReachable, isActive)}
                                disabled={!isReachable}
                                className="pg-jdot"
                                data-state={state}
                                data-reachable={isReachable ? "true" : "false"}
                                aria-label={`Step ${idx + 1}: ${step.label}${isReachable ? "" : " (locked)"}`}
                                data-testid={`step-dot-${step.id}`}
                            >
                                {isDone && !isActive ? <Check className="h-3 w-3" strokeWidth={2.6} /> : (idx + 1)}
                            </button>
                        );
                    })}
                </div>
            </div>
        </>
    );
};

export default InquiryStepper;
